import type { FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardBase, * as Card from '@segment/matcha/recipes/Card'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Empower high-performance teams`,
}

const cards = [
  {
    title: `Evolving into a direct-to-consumer business`,
    description: `Director of Product Engineering, Tory Brady, discusses how Allergan tackled its direct-to-consumer transition and the loyalty program that emerged as a result.`,
    link: `https://vimeo.com/showcase/6546149/video/369446241`,
  },
  {
    title: `How to deliver better insights while protecting privacy`,
    description: `Daryl Bowden, SVP of Technology at Fox Networks, explains how FOX aligned all teams to a common data framework and improved customer insight, all while protecting customer privacy.`,
    link: `https://vimeo.com/showcase/6546149/video/369430226`,
  },
  {
    title: `Customer-driven growth at enterprise scale`,
    description: `Maia Sisk, Director of SaaS Growth, shares the story of how IBM partnered with Segment to supercharge customer nurture and growth initiatives.`,
    link: `https://vimeo.com/showcase/6546149/video/369436294`,
  },
  {
    title: `How AB InBev got 2M businesses buzzing through a next generation B2B platform with Twilio Segment`,
    description: `Learn how AB InBev uses Twilio Segment with their B2B e-commerce platform, BEES, to personalize the customer experience and give retailers the tools and visibility they need to better manage their inventory.`,
    link: `/customers/ab-inbev/`,
  },
]

const EmpowerHighPerformanceTeams: FC = () => {
  const pageConfig = usePageConfig()
  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <Contents>
            <ColumnTrack overrides={{ md: 2 }}>
              {cards.map((card, idx) => (
                <CardBase key={idx} href={getAbsoluteUrl(card.link, pageConfig)}>
                  <Card.Body>
                    <Card.Copy>
                      <Card.Headline tag="h3">{card.title}</Card.Headline>
                      <Card.Description variant="pLarge">{card.description}</Card.Description>
                    </Card.Copy>
                    <Card.Action alignment="right">
                      <ChevronRightIcon />
                    </Card.Action>
                  </Card.Body>
                </CardBase>
              ))}
            </ColumnTrack>
          </Contents>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

export default EmpowerHighPerformanceTeams

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const StyledSection = styled(SectionBase)`
  max-width: 1280px;

  ${Section.Wrapper} {
    row-gap: 40px;

    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      row-gap: 64px;
    }
  }
`

const Contents = styled.div`
  position: relative;
  height: 100%;
`
